import { SAASObject } from '@Types/general.types';

/**
 * Year-only fields (yearLaid, yearInstalled, yearRenewed, yearConstructed, ...)
 * are typed as `Date / Time` in the asset schema, so they used to be run through
 * the date formatting pipeline twice - once in the sagas and again by
 * PropertyCard - which produced `Invalid DateTime`.
 *
 * These helpers normalise such a field to a plain 4 digit year exactly once so
 * it can be rendered as text.
 */

/**
 * Matches on the property id rather than an allow list, because the Properties
 * panel renders whatever `assetSchema.getProperties()` returns - a hardcoded
 * list would silently keep the bug for any year property we forgot.
 */
export const isYearField = (fieldName?: string): boolean =>
	!!fieldName && /^year/.test(fieldName);

const YEAR_ONLY = /^\d{4}$/;
// Assumes the server always sends dates in ISO (`YYYY-MM-DD[...]`) for these
// fields, matching the existing raw-string call sites in the asset sagas.
const LEADING_YEAR = /^(\d{4})-\d{2}-\d{2}/;
// Server sentinel for "no date", which can arrive as the full date or
// already reduced to a bare 4 digit year.
const NO_DATE_YEAR = '1111';

const extractYear = (raw: string): string | undefined => {
	const value = raw.trim();

	if (value.length === 0) return undefined;

	const year = YEAR_ONLY.test(value) ? value : LEADING_YEAR.exec(value)?.[1];
	return year === NO_DATE_YEAR ? undefined : year;
};

/**
 * @returns the 4 digit year, or `undefined` when the value is absent or not
 * recognisable as a year (the UI renders `-` for `undefined`).
 */
export const formatYearFieldValue = (
	value: string | number | SAASObject | null | undefined,
): string | undefined => {
	if (value == null) return undefined;

	if (typeof value === 'object') {
		// Years are marked as `date` in the schema, but the server sends the
		// year itself as a 4 char displayValue. Fall back to `value` if that
		// doesn't turn out to be a usable year.
		const fromDisplayValue =
			value.displayValue?.length === 4
				? extractYear(value.displayValue)
				: undefined;
		if (fromDisplayValue) return fromDisplayValue;

		return value.value == null
			? undefined
			: extractYear(String(value.value));
	}

	return extractYear(String(value));
};
